import DateHelper from '../../../Core/helper/DateHelper.js';
import TimelineChartDataProviderBase from './TimelineChartDataProviderBase.js';

/**
 * @module Gantt/util/chart/GanttDataProvider
 */

/**
 * @callback GanttDataProviderSeriesCallback
 * @param {Gantt.model.TaskModel} task The task to calculate the value for
 * @param {Date} start The start date of the interval
 * @param {Date} end The end date of the interval
 * @param {Number} durationDoneToDate Total working time expired before the start of the current interval
 * @param {Number} durationDone Working time actually spent on the task - duration * percentDone
 * @param {Number} durationInInterval Task working time in the given interval
 * @returns {Number} The value for the certain task in the given period
 */

/**
 * @typedef {Object} SeriesMetaData
 * @property {String} text The text to display in the legend
 * @property {Number} total The total value of the series
 * @property {String} color Any valid CSS color value: https://developer.mozilla.org/en-US/docs/Web/CSS/color_value
 * @property {GanttDataProviderSeriesCallback} callback The function to calculate the value of the series
 */

/**
 * Data source for the timeline chart
 *
 * @extends Gantt/util/chart/TimelineChartDataProviderBase
 */
export default class GanttDataProvider extends TimelineChartDataProviderBase {
    static configurable = {
        /**
         * Default dataset to show.
         *
         * @config {'duration'|'progress'|String}
         * @default
         * @category Data
         */
        defaultDataset : 'duration',

        /**
         * Defines intervals to fill date map. One day by default.
         *
         * @prp {DurationConfig}
         * @internal
         * @default
         */
        unit : '1 day',

        /**
         * This object configures available data series, metadata and a function to calculate the values.
         * Every key is a dataset name, and every value is an {@link SeriesMetaData} object with the following
         * properties:
         * - text - the text to display in the legend
         * - total - the total value of the series
         * - color - the color of the series
         * - callback - the function to calculate the value of the series
         *
         * ```javascript
         * // Metadata object example
         * duration : {
         *     text : 'Duration',
         *     total : 0,
         *     color : '#CC0033',
         *     callback : this.prototype.getTaskDuration
         * }
         * ```
         *
         * ### Color
         *
         * Color is an optional parameter. If not provided, the default color will be used. Series color can be
         * styles with CSS using attribute selector:
         *
         * ```CSS
         * .b-timeline-overlay-chart path[data-series="name"] { stroke: #FF0000; }
         * .b-timeline-overlay-chart circle[data-series="name"] { fill: #FF0000; }
         * ```
         *
         * ### Callback
         *
         * {@link GanttDataProviderSeriesCallback Callback} is used to generate series data. It is called for every leaf
         * task for every interval, specified by {@link #config-unit}, between project start and end dates. For example,
         * to calculate working time of the task in the given period:
         * ```javascript
         * new Gantt({
         *   features : {
         *     timelineChart : {
         *       dataProvider : {
         *         meta : {
         *           duration : {
         *             callback : (task, start, end) {
         *               // default implementation
         *               return task.effectiveCalendar.calculateDurationMs(start, end);
         *             }
         *           }
         *         }
         *       }
         *     }
         *   }
         * });
         * ```
         *
         * @config {Object}
         * @default
         */
        meta : {
            duration : { text : 'Duration', total : 0, color : '#CC0033', callback : this.prototype.getTaskDuration },
            progress : { text : 'Progress', total : 0, color : '#3333FF', callback : this.prototype.getTaskProgress }
        }
    };

    construct({ gantt, config }) {
        this.gantt = gantt;

        super.construct(config);

        this._datesMap = {};
    }

    updateUnit() {
        this.generateDatasets();

        this.trigger('refresh');
    }

    getTaskDuration(task, start, end, durationDoneToDate, durationDone, durationInInterval) {
        return durationInInterval;
    }

    getTaskProgress(task, start, end, durationDoneToDate, durationDone, durationInInterval) {
        return Math.max(0, Math.min(durationDone - durationDoneToDate, durationInInterval));
    }

    get datasets() {
        return Object.entries(this.meta).map(([name, { text, color }]) => ({ name, text, color }));
    }

    /**
     * Returns true if data should be provided with a given view config.
     *
     * @param {TimelineChartViewConfig} viewConfig
     * @param {String} dataset
     * @returns {Boolean}
     * @internal
     */
    shouldReturnDataset(viewConfig, dataset) {
        return viewConfig.projectEndX - viewConfig.projectStartX > 2;
    }

    /**
     * Returns dataset for the chart
     * @param {TimelineChartViewConfig} viewConfig
     * @param {String[]} [datasets] List of dataset names to return
     * @returns {TimelineChartDataset[]}
     */
    getDataset(viewConfig, datasets = [this.defaultDataset]) {
        const
            me                = this,
            { gantt, unit }   = me,
            { timeAxis, rtl } = gantt,
            result            = [],
            axisStartMS       = DateHelper.floor(timeAxis.startDate, unit).getTime(),
            axisEndMS         = DateHelper.ceil(timeAxis.endDate, unit).getTime();

        result.push(...datasets.map(name => {
            return {
                maxValue : 100,
                lineData : [],
                label    : me.meta[name].text,
                color    : me.meta[name].color,
                name
            };
        }));

        if (
            datasets.length &&
            gantt.taskStore.count &&
            gantt.project.startDate && gantt.project.endDate
        ) {
            let tick, intervalStart;

            Object.entries(me._datesMap).forEach(([intervalStartMS, data], datesMapIndex) => {
                // convert to number
                intervalStartMS -= 0;

                if (intervalStartMS > gantt.project.endDate.getTime()) {
                    intervalStartMS = gantt.project.endDate.getTime();
                }

                // Pick entries which match the time axis ticks
                if (
                    // fast track to skip resolving tick for every entry
                    intervalStartMS >= axisStartMS && intervalStartMS <= axisEndMS &&
                    // If map entry does not match time axis tick, we skip it (for now)
                    // What we really need, is to extend the map to fill the data for the missing ticks
                    (tick = timeAxis.getTickFromDate((intervalStart = new Date(intervalStartMS)))) >= 0 &&
                    (tick % 1 === 0 || intervalStart.getTime() === gantt.project.endDate.getTime())
                ) {
                    datasets.forEach((name, datasetIndex) => {
                        // result and datasets arrays are synced
                        const { lineData } = result[datasetIndex];

                        // If current date does not have any real data, take the last one
                        if (Object.keys(data).length === 0) {
                            if (datesMapIndex === 0) {
                                lineData.push({
                                    x     : rtl ? viewConfig.width - viewConfig.projectStartX : viewConfig.projectStartX,
                                    y     : 0,
                                    value : 0,
                                    date  : DateHelper.format(intervalStart, 'YYYY-MM-DD')
                                });
                            }
                            else {
                                lineData.push({ ...lineData[lineData.length - 1] });
                            }
                        }
                        else {
                            const
                                percent = Math.round(data[`${name}_sum`] / me.meta[name].total * 100),
                                lineDef = {
                                    x : Math[datesMapIndex === 0 ? 'max' : 'min'](
                                        datesMapIndex === 0 ? viewConfig.projectStartX : viewConfig.projectEndX,
                                        gantt.timeAxisViewModel.getPositionFromDate(intervalStart)
                                    ),
                                    y     : percent,
                                    percent,
                                    value : data[`${name}_sum`],
                                    date  : DateHelper.format(intervalStart, 'YYYY-MM-DD')
                                };

                            if (rtl) {
                                lineDef.x = viewConfig.width - lineDef.x;
                            }

                            lineData.push(lineDef);
                        }
                    });
                }
            });
        }

        return result;
    }

    //#region Datasets

    generateDatasets() {
        this._datesMap = {};

        const
            me              = this,
            {
                gantt,
                unit,
                _datesMap : datesMap,
                meta
            } = me;

        if (!gantt.project.startDate || !gantt.project.endDate) {
            return;
        }

        const
            { startDate : projectStartDate, endDate : projectEndDate } = gantt.project,
            mapStart = DateHelper.floor(projectStartDate, unit),
            mapEnd = DateHelper.ceil(projectEndDate, unit),
            metrics = Object.keys(meta);

        // Fill dates map for every day in the project duration
        for (let i = mapStart, e = mapEnd.getTime(); i.getTime() <= e; i = DateHelper.add(i, unit)) {
            datesMap[i.getTime()] = {};
            Object.values(meta).forEach(metric => {
                metric.total = 0;
                metric.maxValue = 0;
            });
        }

        // iterate over the task store and fill ticks map with tasks
        gantt.taskStore.forEach(task => {
            if (task.isLeaf && task.isScheduled) {
                const
                    {
                        startDate   : taskStartDate,
                        endDate     : taskEndDate,
                        percentDone : taskPercentDone,
                        durationMS
                    }            = task,
                    doneDuration = durationMS * taskPercentDone,
                    endMS        = DateHelper.ceil(taskEndDate, unit).getTime();

                let intervalStart      = DateHelper.floor(taskStartDate, unit),
                    intervalEnd        = DateHelper.add(intervalStart, unit),
                    intervalEndMS      = intervalEnd.getTime(),
                    durationDoneToDate = 0;

                while (intervalEndMS <= endMS) {
                    const
                        data               = datesMap[intervalEndMS],
                        durationInInterval = task.effectiveCalendar.calculateDurationMs(
                            DateHelper.max(intervalStart, taskStartDate),
                            DateHelper.min(intervalEnd, taskEndDate)
                        );

                    durationDoneToDate += durationInInterval;

                    metrics.forEach(name => {
                        const
                            metric = meta[name],
                            value  = metric.callback.call(me, task, intervalStart, intervalEnd,
                                durationDoneToDate, doneDuration, durationInInterval);

                        data[`${name}_value`] = (data[`${name}_value`] ?? 0) + value;
                    });

                    intervalStart = intervalEnd;
                    intervalEnd = DateHelper.add(intervalEnd, unit);
                    intervalEndMS = intervalEnd.getTime();
                }
            }
        });

        Object.entries(datesMap).reduce((previous, [key, current]) => {
            Object.keys(meta).forEach(metric => {
                const value = current[`${metric}_value`];

                if (value != null) {
                    current[`${metric}_sum`] = meta[metric].total += value;
                }
            });

            if (Object.keys(current).length === 0 && Object.keys(previous).length > 0) {
                Object.assign(current, previous);
            }

            return current;
        });
    }

    //#endregion
}
